import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Bot, Zap, ArrowRight, Inbox } from "lucide-react";
import { ASSETS } from "../config/brand";
import { dataApi, formatApiError } from "../api/client";

const TONE_CHIP = { indigo: "chip-indigo", success: "chip-success", info: "chip-info", warning: "chip-warning", danger: "chip-danger" };
const TONE_COLOR = { indigo: "#3949AB", success: "#107C10", info: "#0078D4", warning: "#F7A31C", danger: "#D13438" };

const KPI_LABELS = {
  carreras_hoy: "Carreras hoy",
  ingresos_hoy: "Ingresos hoy",
  usuarios_activos: "Usuarios activos",
  cancelaciones_hoy: "Cancelaciones hoy",
};

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [rides, setRides] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([dataApi.kpis(), dataApi.rides()])
      .then(([k, r]) => { setKpis(k); setRides(r); })
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  const today = new Date().toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-view">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#3949AB]">Centro de control</div>
        <h1 className="font-display font-bold text-2xl text-neutral-900 mt-0.5">Buenos días</h1>
        <p className="text-sm text-neutral-500 mt-1 capitalize">Resumen de operaciones · {today}</p>
      </div>

      {err && <div className="chip chip-danger">{err}</div>}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-kpis">
        {kpis ? Object.entries(kpis).map(([key, k]) => {
          const neg = String(k.delta).startsWith("-");
          return (
            <div key={key} className="office-card p-4" data-testid={`kpi-${key}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  {KPI_LABELS[key] || key}
                </div>
                <div className={`chip ${TONE_CHIP[k.tone] || "chip-neutral"}`}>
                  {neg ? <TrendingDown className="w-3 h-3" strokeWidth={2.5}/> : <TrendingUp className="w-3 h-3" strokeWidth={2.5}/>}
                  {k.delta}
                </div>
              </div>
              <div className="font-display font-bold text-3xl text-neutral-900 leading-none tracking-tight">
                {k.value}
              </div>
              <div className="mt-2 h-1 rounded-full" style={{ backgroundColor: TONE_COLOR[k.tone] }}></div>
            </div>
          );
        }) : Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="office-card p-4 animate-pulse">
            <div className="h-3 bg-neutral-100 rounded w-24 mb-3"></div>
            <div className="h-8 bg-neutral-100 rounded w-16"></div>
          </div>
        ))}
      </div>

      {/* Chart + Map placeholder */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 office-card p-5">
          <div className="mb-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Tendencia semanal</div>
            <h2 className="font-display font-bold text-lg text-neutral-900">Carreras e ingresos</h2>
          </div>
          <EmptyState
            icon={TrendingUp}
            title="Aún no hay datos de carreras"
            desc="El gráfico se poblará automáticamente cuando comiencen a registrarse carreras."
          />
        </div>

        <div className="col-span-12 lg:col-span-4 office-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Flota en vivo</div>
              <h2 className="font-display font-bold text-lg text-neutral-900">Mapa de unidades</h2>
            </div>
            <span className="chip chip-success">
              <span className="w-1.5 h-1.5 bg-[#107C10] rounded-full pulse-indigo"></span> LIVE
            </span>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-neutral-200 h-56">
            <img src={ASSETS.cityMap} alt="Tacna" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
              <div className="text-center px-4">
                <div className="text-sm font-semibold text-neutral-700">Sin conductores en línea</div>
                <div className="text-xs text-neutral-500 mt-1">Aparecerán en el mapa cuando se conecten desde la app</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bot WhatsApp */}
      <div className="office-card p-5 border-l-4 border-[#3949AB]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#EEF0FC] rounded-lg flex items-center justify-center">
              <Bot className="w-6 h-6 text-[#3949AB]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#3949AB] flex items-center gap-1.5">
                <Zap className="w-3 h-3" strokeWidth={3}/> Bot WhatsApp
              </div>
              <h3 className="font-display font-bold text-lg text-neutral-900">Módulo listo — pendiente conectar WhatsApp Business API</h3>
              <div className="text-sm text-neutral-500">Se activará automáticamente cuando Meta apruebe la cuenta.</div>
            </div>
          </div>
          <button className="btn-secondary">Configurar <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5}/></button>
        </div>
      </div>

      {/* Recent rides */}
      <div className="office-card overflow-hidden">
        <div className="p-5 pb-3 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Actividad reciente</div>
            <h2 className="font-display font-bold text-lg text-neutral-900">Últimas carreras</h2>
          </div>
        </div>
        {(rides === null) ? (
          <div className="p-8 text-center text-sm text-neutral-500">Cargando...</div>
        ) : rides.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Sin carreras registradas"
            desc="Cuando los conductores acepten solicitudes desde la app o el bot de WhatsApp, aparecerán aquí."
            small
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="office-table">
              <thead>
                <tr>
                  <th>Hora</th><th>Pasajero</th><th>Conductor</th><th>Destino</th><th>Monto</th><th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {rides.slice(0, 10).map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono-grotesk text-neutral-600">{r.requested_at?.slice(11, 16)}</td>
                    <td>{r.passenger_id}</td>
                    <td>{r.driver_id || "—"}</td>
                    <td>{r.destination_address}</td>
                    <td className="font-mono-grotesk font-semibold text-[#3949AB]">S/ {r.fare_pen}</td>
                    <td><span className="chip chip-neutral">{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export const EmptyState = ({ icon: Icon, title, desc, small }) => (
  <div className={`text-center ${small ? "py-8" : "py-12"} px-6`}>
    <div className="w-14 h-14 mx-auto mb-3 bg-[#EEF0FC] rounded-full flex items-center justify-center">
      <Icon className="w-6 h-6 text-[#3949AB]" strokeWidth={2} />
    </div>
    <div className="font-semibold text-neutral-900 text-sm">{title}</div>
    <div className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">{desc}</div>
  </div>
);
